import React from "react";
import { toast } from "sonner";
import { Check, Copy, ExternalLink } from "lucide-react";
import { Button } from "../../ui/button";

// BazookaResultPanel — renders the last-fire result card: delivery
// status, pre-picked username, magic invite URL with copy button,
// and (for X DM mode) the paste-ready 280-char template + a
// deep-link to x.com's compose UI. Extracted from ChairmansBazooka
// (Iter-58.6 split) so the result UI is isolated from the form
// orchestration.
//
// Copy handlers live here (each owns its own clipboard call + toast)
// so the parent doesn't have to thread copy functions through props.
const BazookaResultPanel = ({ lastResult }) => {
  if (!lastResult) return null;

  const copyUrl = async () => {
    if (!lastResult.invite_url) return;
    try {
      await navigator.clipboard.writeText(lastResult.invite_url);
      toast.success("Invite URL copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — long-press the URL to copy manually.");
    }
  };

  const copyDmTemplate = async () => {
    if (!lastResult.dm_template) return;
    try {
      await navigator.clipboard.writeText(lastResult.dm_template);
      toast.success("DM template copied — paste it into x.com.");
    } catch {
      toast.error("Couldn't copy — select the text manually.");
    }
  };

  return (
    <div
      data-testid="bazooka-last-result"
      className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 p-3 text-sm"
    >
      <p className="text-emerald-300 font-semibold mb-1 inline-flex items-center gap-1.5">
        {lastResult.delivered ? <Check className="h-4 w-4" /> : null}
        {lastResult.delivered ? "Delivered" : "Recorded (delivery unconfirmed)"}
        <span className="text-gray-400 font-normal text-xs">
          · expires {new Date(lastResult.expires_at).toLocaleDateString()}
        </span>
      </p>
      {lastResult.username && (
        <p className="text-amber-200 text-xs mb-1">
          Username pre-picked:{" "}
          <code className="bg-gray-900 px-1.5 py-0.5 rounded">
            @{lastResult.username}
          </code>
          <span className="text-gray-500">
            {" "}— recipient skips the username step.
          </span>
        </p>
      )}
      <p className="text-gray-300 text-xs">
        Magic-link (share if delivery failed):
      </p>
      <div className="flex gap-2 items-center mt-1">
        <code
          className="flex-1 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-emerald-200 text-xs overflow-x-auto whitespace-nowrap"
          data-testid="bazooka-invite-url"
        >
          {lastResult.invite_url}
        </code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={copyUrl}
          className="shrink-0"
        >
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy
        </Button>
      </div>
      {/* X-DM only: paste-ready 280-char message + a deep-link to
          x.com's compose UI. The compose-URL approach pre-fills the
          message but leaves the recipient field blank so the admin
          can pick the target (their X is logged in already, so it's
          one click after paste). */}
      {lastResult.dm_template && (
        <div className="mt-3 pt-3 border-t border-emerald-500/20">
          <p className="text-emerald-200 text-xs font-medium mb-1">
            X DM template (copy → paste into x.com):
          </p>
          <pre
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-gray-200 text-xs whitespace-pre-wrap font-sans leading-snug"
            data-testid="bazooka-dm-template"
          >
            {lastResult.dm_template}
          </pre>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyDmTemplate}
              data-testid="bazooka-copy-dm"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy DM
            </Button>
            {lastResult.x_compose_url && (
              <a
                href={lastResult.x_compose_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-black hover:bg-gray-900 text-white text-xs font-medium border border-gray-700"
                data-testid="bazooka-open-x-compose"
              >
                <ExternalLink className="h-3 w-3" /> Open x.com compose
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BazookaResultPanel;
